import React, { useEffect, useState } from "react";
import { PageHeader, Toast } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { lock } from "@/lib/perm";
import api from "@/lib/api";
import { THEMES, THEME_LABELS, setInstallSettings, previewTheme, clearUserTheme } from "@/lib/uiTheme";

const errText = (e) => e?.response?.data?.detail || "Eroare";

// Machete mici ale celor trei aspecte (desenate cu CSS, independente de aspectul activ)
function Preview({ kind }) {
  const box = { display: "flex", height: 150, border: "1px solid #D9D9D9", borderRadius: 10, overflow: "hidden", fontFamily: "system-ui", position: "relative" };
  if (kind === "fiord") {
    return (
      <div style={{ ...box, background: "#E6F0F4", flexDirection: "column" }} aria-hidden="true">
        <div style={{ height: 22, display: "flex", alignItems: "center", padding: "0 8px", gap: 6 }}>
          <div style={{ width: 30, height: 10, borderRadius: 3, background: "#10303D" }} /><div style={{ flex: 1 }} /><div style={{ width: 14, height: 14, border: "1.5px solid #4A6874", borderRadius: 5, background: "#fff" }} /></div>
        <div style={{ padding: "2px 8px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {["Panou", "Angajați", "Concedii", "Dosar"].map(t => <div key={t} style={{ border: "1.5px solid #A9D3D6", borderRadius: 8, height: 26, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#10303D", background: "#fff" }}>{t}</div>)}
        </div>
        <div style={{ position: "absolute", left: 14, right: 14, bottom: 8, height: 28, background: "#fff", border: "1px solid #C5D8DF", borderRadius: 14, display: "flex", gap: 4, padding: 3, boxShadow: "0 4px 10px rgba(16,48,61,.18)" }}>
          {[1, 0, 0, 0, 0].map((a, i) => <div key={i} style={{ flex: 1, borderRadius: 11, background: a ? "#0B7F81" : "#E3EEF2" }} />)}
        </div>
      </div>);
  }
  if (kind === "purple") {
    return (
      <div style={{ ...box, background: "#F5F5F5", flexDirection: "column" }} aria-hidden="true">
        <div style={{ height: 22, background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,.12)", display: "flex", alignItems: "center", padding: "0 8px", gap: 6 }}>
          <div style={{ width: 14, height: 14, border: "1.5px solid #6B6B6B", borderRadius: 4 }} /><div style={{ flex: 1 }} /><div style={{ width: 14, height: 14, border: "1.5px solid #6B6B6B", borderRadius: "50%" }} /></div>
        <div style={{ padding: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {["Panou", "Cont", "Concedii", "Dosar"].map(t => <div key={t} style={{ border: "1.5px solid #E3C1F5", borderRadius: 8, height: 30, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#1F1F1F", background: "#fff" }}>{t}</div>)}
        </div>
        <div style={{ position: "absolute", right: 8, bottom: 8, width: 44, height: 12, borderRadius: 6, background: "linear-gradient(135deg,#C23B7E,#7C2BD0)" }} />
      </div>);
  }
  return (
    <div style={{ ...box, background: "#F8FAFC" }} aria-hidden="true">
      <div style={{ width: 58, background: "#fff", borderRight: "1px solid #E2E8F0", padding: 6, display: "flex", flexDirection: "column", gap: 5 }}>
        <div style={{ height: 10, borderRadius: 3, background: "linear-gradient(90deg,#0A1930,#1E3A8A)" }} />
        {[1, 2, 3, 4].map(i => <div key={i} style={{ height: 7, borderRadius: 3, background: i === 1 ? "#0A1930" : "#E2E8F0" }} />)}
      </div>
      <div style={{ flex: 1, padding: 8 }}>
        <div style={{ height: 10, width: 70, background: "#0A1930", borderRadius: 3, marginBottom: 8 }} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>{[1, 2, 3, 4].map(i => <div key={i} style={{ height: 30, border: "1px solid #E2E8F0", borderRadius: 6, background: "#fff" }} />)}</div>
      </div>
    </div>
  );
}

const DESCRIPTIONS = {
  fiord: "Aspectul implicit la instalările noi: meniu plutitor jos (patru pagini + „Mai mult”), pagină de start cu plăci, acțiuni de rând în meniul ⋮.",
  navy: "Aspectul clasic: meniu lateral albastru închis, tabele clasice, butoane mici pe rânduri.",
  purple: "Bară sus și meniu sertar, pagină de start cu plăci, cale de navigare, acțiuni de rând în meniul ⋮; în mov și roz.",
};

/** Aspectul interfeței (doar prezentare): Adminul alege implicitul instalării și dacă utilizatorii își pot alege singuri aspectul. */
export default function Appearance() {
  const { has } = useAuth();
  const canEdit = has("config.update");
  const [theme, setTheme] = useState("fiord");
  const [choice, setChoice] = useState(false);
  const [saved, setSaved] = useState(null);
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const toast = (text, type = "info") => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "" }), type === "error" ? 8000 : 3500); };
  useEffect(() => { api.get("/ui-settings").then(r => { setTheme(r.data.ui_theme === "orange" ? "purple" : r.data.ui_theme); setChoice(r.data.user_choice); setSaved(r.data); }).catch(e => toast(errText(e), "error")); }, []);
  const save = async () => {
    try {
      const r = await api.put("/ui-settings", { ui_theme: theme, user_choice: choice });
      setSaved(r.data); clearUserTheme(); setInstallSettings(r.data);
      toast("Aspectul a fost salvat pentru toată instalarea", "success");
    } catch (e) { toast(errText(e), "error"); }
  };
  const dirty = saved && (saved.ui_theme !== theme || saved.user_choice !== choice);
  return (
    <>
      <PageHeader title="Aspect interfață" subtitle="Alege cum arată platforma pentru toți utilizatorii. Funcționalitățile sunt identice în toate aspectele; Superadmin vede mereu aspectul Navy." />
      <Toast msg={msg.text} type={msg.type} />
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }} role="radiogroup" aria-label="Aspect implicit">
          {THEMES.map(t => (
            <label key={t} data-testid={`appearance-${t}`} style={{ cursor: canEdit ? "pointer" : "default", border: `2px solid ${theme === t ? "var(--blue-primary)" : "#E2E8F0"}`, borderRadius: 14, padding: 12, display: "block", background: "#fff" }}>
              <Preview kind={t} />
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                <input type="radio" name="ui-theme" value={t} checked={theme === t} disabled={!canEdit} onChange={() => setTheme(t)} data-testid={`appearance-radio-${t}`} />
                <b>{THEME_LABELS[t]}</b>
              </div>
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>{DESCRIPTIONS[t]}</div>
            </label>))}
        </div>
        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 18, fontSize: 14 }}>
          <input type="checkbox" checked={choice} disabled={!canEdit} onChange={e => setChoice(e.target.checked)} data-testid="appearance-user-choice" style={{ marginTop: 3 }} />
          <span>Fiecare utilizator își poate schimba singur aspectul (din meniul lui). Dacă debifezi, toți văd aspectul ales mai sus.</span>
        </label>
        <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn btn-primary" {...lock(canEdit)} disabled={!canEdit || !dirty} onClick={save} data-testid="appearance-save">Salvează</button>
          <button className="btn btn-secondary" onClick={() => previewTheme(theme)} data-testid="appearance-try" title="Vezi aspectul ales, doar în acest browser, fără să-l salvezi pentru toți">Previzualizează în acest browser</button>
        </div>
      </div>
    </>
  );
}
